import type { AbaPrincipal } from '../pages/corte/abasModulo';
import { nwDisponivel } from './nwModulosJanela';

export const SYNC_CHANNEL = 'ade-desktop-v1';

/** Chaves de dados compartilhados entre janelas NW.js. */
export type SyncChave =
  | 'catalogo'
  | 'relatorio'
  | 'historico-impressoes'
  | 'lotes-produtos'
  | 'tempo-corte-produtos'
  | 'tempo-corte-itens'
  | 'sobra'
  | 'sobra-pastas'
  | 'sobra-riscados'
  | 'layout-impressao'
  | 'auditoria'
  | 'pontuacoes-produtos';

export type MensagemSyncDesktop = {
  tipo: 'ade-sync';
  chave: SyncChave;
  token: number;
  origem?: string;
};

const origemJanela =
  typeof window !== 'undefined'
    ? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    : 'ssr';

/** Avisa outras janelas que um dado compartilhado mudou. */
export function notificarSyncDesktop(chave: SyncChave): void {
  const msg: MensagemSyncDesktop = {
    tipo: 'ade-sync',
    chave,
    token: Date.now(),
    origem: origemJanela,
  };

  try {
    const bc = new BroadcastChannel(SYNC_CHANNEL);
    bc.postMessage(msg);
    bc.close();
  } catch {
    /* ignora */
  }

  try {
    const runtime = (window as unknown as {
      nw?: { Window?: { getAll?: () => Array<{ window?: Window }> } };
    }).nw;
    for (const janela of runtime?.Window?.getAll?.() ?? []) {
      try {
        janela.window?.postMessage(msg, '*');
      } catch {
        /* ignora */
      }
    }
  } catch {
    /* ignora */
  }
}

export function escutarSyncDesktop(
  onSync: (chave: SyncChave, token: number) => void
): () => void {
  const tratar = (data: unknown) => {
    const m = data as MensagemSyncDesktop;
    if (m?.tipo !== 'ade-sync' || !m.chave) return;
    if (m.origem === origemJanela) return;
    onSync(m.chave, m.token);
  };

  const onWindowMessage = (e: MessageEvent) => tratar(e.data);
  window.addEventListener('message', onWindowMessage);

  let bc: BroadcastChannel | null = null;
  try {
    bc = new BroadcastChannel(SYNC_CHANNEL);
    bc.onmessage = (e) => tratar(e.data);
  } catch {
    /* ignora */
  }

  return () => {
    window.removeEventListener('message', onWindowMessage);
    bc?.close();
  };
}

export type DadoModuloChave =
  | 'catalogo'
  | 'relatorio'
  | 'historico'
  | 'lotes'
  | 'tempoCorte'
  | 'sobra'
  | 'layout'
  | 'auditoria'
  | 'pontuacoes';

/** Quais blobs carregar no mount de uma janela filha (performance). */
export function dadosNecessariosModulo(modulo: AbaPrincipal | null): 'todos' | Set<DadoModuloChave> {
  if (!modulo) return 'todos';

  switch (modulo) {
    case 'catalogo':
      return new Set(['catalogo']);
    case 'projeto':
      return new Set(['catalogo', 'layout', 'historico', 'auditoria']);
    case 'resultadosCopa':
      return new Set(['auditoria']);
    case 'relatorio':
      return new Set(['relatorio', 'tempoCorte', 'catalogo', 'auditoria']);
    case 'historico':
      return new Set(['historico', 'auditoria']);
    case 'sobra':
      return new Set(['sobra', 'auditoria']);
    case 'tempoCorte':
    case 'analiseTempo':
    case 'tempoProducaoPeca':
    case 'adiantamentoMdf':
      return new Set(['tempoCorte', 'lotes', 'auditoria']);
    case 'lotes':
    case 'produtoPorPalete':
      return new Set(['lotes', 'auditoria']);
    case 'boletimPontuacao':
    case 'boletimCalculadora':
    case 'boletimLinhaFabrica':
    case 'boletimTabelaDias':
    case 'boletimResumoDias':
    case 'boletimGrafico':
    case 'tempoEmbalar':
      return new Set(['lotes', 'tempoCorte', 'pontuacoes', 'auditoria']);
    default:
      return new Set(['auditoria']);
  }
}

export function moduloPrecisaDado(
  escopo: 'todos' | Set<DadoModuloChave>,
  dado: DadoModuloChave
): boolean {
  return escopo === 'todos' || escopo.has(dado);
}

/**
 * Componentes pesados só montam na janela certa.
 * NW.js multi-janela: a principal (Projeto) NÃO monta Copa em segundo plano —
 * isso gravava estado velho por cima do que a janela filha salvou ao fechar.
 */
export function montarComponentePesado(
  isJanelaFilha: boolean,
  moduloFixo: AbaPrincipal | null,
  moduloComponente: AbaPrincipal,
  abaAtivaPrincipal: AbaPrincipal = 'projeto'
): boolean {
  if (isJanelaFilha) return moduloFixo === moduloComponente;
  if (nwDisponivel()) return abaAtivaPrincipal === moduloComponente;
  return true;
}

/** Escopo de persistência da janela principal no desktop (evita carregar tudo com `todos`). */
export function moduloEscopoJanelaPrincipal(abaAtiva: AbaPrincipal): AbaPrincipal {
  if (abaAtiva === 'resultadosCopa') return 'resultadosCopa';
  return 'projeto';
}
